/**
 * Persona Builder workspace — an interactive, agent-assisted "author a new
 * persona" experience. Opened as a full-screen overlay from the Personas page.
 *
 *   Launcher  → describe the persona + Start (or resume an in-progress build)
 *   Workspace → chat transcript (left) + live staging tree & file viewer (right),
 *               status pill, send-next-turn composer, Cancel, Approve & Create.
 *   CreateForm→ operator confirms name/agent/mention_names/skills (prefilled from
 *               the staged persona.json) and optionally binds a route, then
 *               provisions the full persona.
 *
 * Mirrors SkillBuilder.tsx; the difference is the final step — a persona is a
 * structural object, so "create" is a confirmation form, not a one-click import.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { UserPlus, X, Send, FileText, Ban, UserCheck, Loader2, ArrowLeft, Wrench } from "lucide-react";
import { PersonaBuilderApi, BridgeApi, CatalogApi } from "../lib/api";
import { qk } from "../lib/query";
import { useToast } from "../lib/toast";
import { Button, Pill, Spinner, EmptyState } from "./primitives";
import { cn } from "../lib/cn";
import type { BuilderMeta, BuilderStatus, PersonaManifest, PersonaCreateInput } from "../lib/types";

const STATUS_TONE: Record<BuilderStatus, "neutral" | "success" | "warning" | "danger"> = {
  running: "warning", awaiting_user: "success", failed: "danger", cancelled: "neutral",
};
const STATUS_LABEL: Record<BuilderStatus, string> = {
  running: "thinking…", awaiting_user: "ready", failed: "failed", cancelled: "cancelled",
};

interface Msg { role: "assistant" | "tool" | "user"; text: string }

/** Parse the stream-json NDJSON log into a readable transcript. */
function parseTranscript(log: string): Msg[] {
  const out: Msg[] = [];
  for (const line of log.split("\n")) {
    if (!line.trim()) continue;
    let ev: Record<string, unknown>;
    try { ev = JSON.parse(line); } catch { continue; }
    if (ev.type === "assistant") {
      const content = (ev.message as { content?: unknown[] })?.content ?? [];
      for (const c of content as Array<Record<string, unknown>>) {
        if (c.type === "text" && typeof c.text === "string" && c.text.trim()) out.push({ role: "assistant", text: c.text });
        else if (c.type === "tool_use") {
          const fp = (c.input as { file_path?: string })?.file_path;
          out.push({ role: "tool", text: fp ? `drafted ${fp}` : String(c.name ?? "tool") });
        }
      }
    }
  }
  return out;
}

export function PersonaBuilder({ onClose, onCreated }: { onClose: () => void; onCreated: (name: string) => void }) {
  const [activeId, setActiveId] = useState<string | null>(null);
  // Lock the page behind the overlay so scrolling stays inside the builder
  // (otherwise touch/wheel chains through to the page underneath).
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);
  return (
    <div className="fixed inset-0 z-50 bg-bg/90 backdrop-blur-sm flex flex-col">
      <div className="h-11 flex items-center gap-2 px-4 border-b border-border flex-shrink-0">
        <UserPlus className="size-4 text-accent" />
        <span className="text-sm font-medium text-fg">Persona Builder</span>
        {activeId && <button onClick={() => setActiveId(null)} className="ml-2 inline-flex items-center gap-1 text-xs text-fg-muted hover:text-fg"><ArrowLeft className="size-3" /> all builds</button>}
        <Button size="icon" variant="ghost" className="ml-auto" onClick={onClose} aria-label="Close"><X className="size-4" /></Button>
      </div>
      <div className="flex-1 min-h-0">
        {activeId
          ? <Workspace id={activeId} onCreated={(n) => { onCreated(n); onClose(); }} />
          : <Launcher onOpen={setActiveId} />}
      </div>
    </div>
  );
}

function Launcher({ onOpen }: { onOpen: (id: string) => void }) {
  const toast = useToast();
  const [goal, setGoal] = useState("");
  const { data: builders = [] } = useQuery({ queryKey: qk.personaBuilder.list, queryFn: PersonaBuilderApi.list, refetchInterval: 4000 });
  const startMut = useMutation({
    mutationFn: () => PersonaBuilderApi.start(goal.trim()),
    onSuccess: (m) => onOpen(m.id),
    onError: (e) => toast.error("Couldn't start", String(e)),
  });
  const inProgress = builders.filter((b) => b.status === "running" || b.status === "awaiting_user");

  return (
    <div className="h-full overflow-y-auto overscroll-contain">
      <div className="max-w-2xl mx-auto px-6 py-8">
        <h2 className="text-lg font-semibold text-fg">Create a persona with an agent</h2>
        <p className="text-sm text-fg-muted mt-1">Describe the persona — its purpose, who it talks to, and its personality. A sandboxed helper (claude-sub) will clarify, draft a SOUL + manifest, and iterate with you. Nothing goes live until you review and create it.</p>
        <textarea
          value={goal}
          onChange={(e) => setGoal(e.target.value)}
          rows={4}
          placeholder="e.g. A warm, concise concierge persona for the AYANA Bali front-desk WhatsApp group that answers guest questions and escalates anything billing-related."
          className="mt-4 w-full text-sm rounded-md bg-surface-elevated border border-border p-3 focus:border-border-strong resize-none"
        />
        <div className="mt-3 flex items-center gap-3">
          <Button onClick={() => startMut.mutate()} disabled={goal.trim().length < 8} loading={startMut.isPending}>
            <UserPlus className="size-3.5" /> Start building
          </Button>
          <span className="text-xs text-fg-subtle">backend: claude-sub (subscription)</span>
        </div>

        {inProgress.length > 0 && (
          <div className="mt-8">
            <div className="text-xs uppercase tracking-wide text-fg-subtle mb-2">In progress</div>
            <ul className="space-y-1.5">
              {inProgress.map((b) => (
                <li key={b.id}>
                  <button onClick={() => onOpen(b.id)} className="w-full text-left rounded-md border border-border bg-surface-elevated hover:bg-surface-2 px-3 py-2 flex items-center gap-2">
                    <span className="text-sm text-fg truncate flex-1">{b.goal}</span>
                    <Pill tone={STATUS_TONE[b.status]}>{STATUS_LABEL[b.status]}</Pill>
                    <span className="text-[11px] text-fg-subtle font-mono">{b.turns}t</span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}

function Workspace({ id, onCreated }: { id: string; onCreated: (name: string) => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [draft, setDraft] = useState("");
  const [pendingUser, setPendingUser] = useState<string[]>([]);
  const [viewFile, setViewFile] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [mobileTab, setMobileTab] = useState<"chat" | "files">("chat");
  const scrollRef = useRef<HTMLDivElement>(null);

  const { data } = useQuery({
    queryKey: qk.personaBuilder.detail(id),
    queryFn: () => PersonaBuilderApi.get(id),
    refetchInterval: (q) => (q.state.data?.meta.status === "running" ? 1500 : false),
  });
  const meta: BuilderMeta | undefined = data?.meta;
  const status = meta?.status;
  const transcript = useMemo(() => parseTranscript(data?.log ?? ""), [data?.log]);
  const staged = data?.staged ?? [];
  const ready = staged.includes("SOUL.md") && staged.includes("persona.json");

  useEffect(() => { if (status === "awaiting_user") setPendingUser([]); }, [status, transcript.length]);
  useEffect(() => { scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight }); }, [transcript.length, pendingUser.length, status]);
  useEffect(() => { if (!viewFile && staged.includes("SOUL.md")) setViewFile("SOUL.md"); }, [staged, viewFile]);

  const turnMut = useMutation({
    mutationFn: (msg: string) => PersonaBuilderApi.turn(id, msg),
    onSuccess: (r, msg) => {
      if (!r.ok) { toast.error("Couldn't send", r.error); return; }
      setPendingUser((p) => [...p, msg]);
      setDraft("");
      qc.invalidateQueries({ queryKey: qk.personaBuilder.detail(id) });
    },
  });
  const cancelMut = useMutation({
    mutationFn: () => PersonaBuilderApi.cancel(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.personaBuilder.detail(id) }),
  });

  const busy = status === "running";
  const send = () => { if (draft.trim() && !busy) turnMut.mutate(draft.trim()); };

  if (!meta) return <div className="h-full flex items-center justify-center"><Spinner /></div>;

  return (
    <div className="h-full flex flex-col min-h-0">
      {/* Mobile pane switcher (desktop shows both side-by-side) */}
      <div className="md:hidden flex flex-shrink-0 border-b border-border">
        <button type="button" onClick={() => setMobileTab("chat")} className={cn("flex-1 h-9 text-xs font-medium", mobileTab === "chat" ? "text-fg border-b-2 border-accent" : "text-fg-muted")}>Chat</button>
        <button type="button" onClick={() => setMobileTab("files")} className={cn("flex-1 h-9 text-xs font-medium", mobileTab === "files" ? "text-fg border-b-2 border-accent" : "text-fg-muted")}>Files · {staged.length}</button>
      </div>
      <div className="flex-1 min-h-0 flex flex-col md:flex-row">
      {/* Chat */}
      <div className={cn("flex-1 min-w-0 min-h-0 flex-col border-border", mobileTab === "chat" ? "flex" : "hidden", "md:flex md:border-r")}>
        <div className="px-4 py-2.5 border-b border-border flex items-center gap-2">
          <span className="text-sm text-fg truncate flex-1" title={meta.goal}>{meta.goal}</span>
          {status && <Pill tone={STATUS_TONE[status]}>{STATUS_LABEL[status]}</Pill>}
        </div>
        <div ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-4 py-4 space-y-3">
          <Bubble role="user" text={meta.goal} />
          {transcript.map((m, i) => <Bubble key={i} role={m.role} text={m.text} />)}
          {pendingUser.map((t, i) => <Bubble key={`p${i}`} role="user" text={t} />)}
          {busy && <div className="flex items-center gap-2 text-xs text-fg-muted"><Loader2 className="size-3.5 animate-spin" /> drafting…</div>}
          {status === "failed" && <div className="text-xs text-danger">Turn failed: {meta.error}</div>}
        </div>
        <div className="p-3 border-t border-border">
          <div className="flex items-end gap-2">
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); send(); } }}
              rows={2}
              placeholder={busy ? "agent is working…" : status === "cancelled" ? "build cancelled" : "Reply, refine, or ask to adjust…  (⌘/Ctrl+Enter)"}
              disabled={busy || status === "cancelled"}
              className="flex-1 text-sm rounded-md bg-surface-elevated border border-border p-2 resize-none focus:border-border-strong disabled:opacity-50"
            />
            <Button onClick={send} disabled={!draft.trim() || busy || status === "cancelled"} loading={turnMut.isPending}><Send className="size-3.5" /></Button>
          </div>
        </div>
      </div>

      {/* Staging tree + viewer + actions */}
      <div className={cn("flex-1 md:flex-none md:w-[44%] md:max-w-2xl flex-col min-h-0", mobileTab === "files" ? "flex" : "hidden", "md:flex")}>
        <div className="px-4 py-2.5 border-b border-border flex items-center gap-2">
          <span className="text-xs uppercase tracking-wide text-fg-subtle">Staged · {staged.length} file{staged.length === 1 ? "" : "s"}</span>
          <div className="ml-auto flex items-center gap-1.5">
            {(status === "running" || status === "awaiting_user") && (
              <Button size="sm" variant="ghost" onClick={() => { if (confirm("Cancel this build? Staged drafts are discarded.")) cancelMut.mutate(); }}>
                <Ban className="size-3.5" /> Cancel
              </Button>
            )}
            <Button size="sm" onClick={() => setShowCreate(true)} disabled={!ready || busy}>
              <UserCheck className="size-3.5" /> Approve &amp; Create
            </Button>
          </div>
        </div>
        <div className="flex-1 min-h-0 flex flex-col md:flex-row">
          <div className="flex-shrink-0 md:w-48 border-b md:border-b-0 md:border-r border-border overflow-x-auto md:overflow-x-visible md:overflow-y-auto overscroll-contain">
            {staged.length === 0 ? <div className="p-3 text-xs text-fg-muted">No drafts yet</div> : (
              <ul className="flex md:block gap-1 md:gap-0 p-1 md:py-1 md:px-0">
                {staged.map((f) => (
                  <li key={f} className="flex-shrink-0">
                    <button onClick={() => setViewFile(f)} className={cn("flex items-center gap-2 px-3 py-1.5 text-xs text-left hover:bg-surface-2 font-mono whitespace-nowrap rounded md:rounded-none md:w-full", viewFile === f ? "bg-surface-2 text-fg" : "text-fg-muted")}>
                      <FileText className="size-3 flex-shrink-0 text-fg-subtle" />
                      <span className="truncate max-w-[45vw] md:max-w-none">{f}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="flex-1 min-w-0 min-h-0">
            {viewFile ? <FileViewer id={id} path={viewFile} rev={data?.log.length ?? 0} /> : (
              <div className="h-full flex items-center justify-center px-6 text-center">
                <EmptyState icon={<Wrench className="size-7" />} title="Staging is empty" description="As the agent drafts SOUL.md + persona.json they'll appear here. Review them before creating." />
              </div>
            )}
          </div>
        </div>
      </div>
      </div>

      {showCreate && <CreateForm id={id} onClose={() => setShowCreate(false)} onCreated={onCreated} />}
    </div>
  );
}

function FileViewer({ id, path, rev }: { id: string; path: string; rev: number }) {
  const { data, isLoading } = useQuery({ queryKey: [...qk.personaBuilder.detail(id), "file", path, rev], queryFn: () => PersonaBuilderApi.file(id, path) });
  return (
    <div className="h-full flex flex-col min-h-0">
      <div className="px-3 py-2 border-b border-border text-xs text-fg-muted font-mono">{path}</div>
      {isLoading ? <div className="flex-1 flex items-center justify-center"><Spinner /></div>
        : <pre className="flex-1 min-h-0 overflow-auto overscroll-contain p-3 text-xs font-mono text-fg whitespace-pre-wrap">{data?.content ?? ""}</pre>}
    </div>
  );
}

/** Operator-confirmed provisioning form, prefilled from the staged persona.json. */
function CreateForm({ id, onClose, onCreated }: { id: string; onClose: () => void; onCreated: (name: string) => void }) {
  const toast = useToast();

  // Prefill from the staged manifest.
  const { data: manifestFile } = useQuery({ queryKey: [...qk.personaBuilder.detail(id), "manifest"], queryFn: () => PersonaBuilderApi.file(id, "persona.json") });
  const manifest: PersonaManifest = useMemo(() => {
    try { return manifestFile ? JSON.parse(manifestFile.content) as PersonaManifest : {}; } catch { return {}; }
  }, [manifestFile]);

  // Reference data for the pickers.
  const { data: cfg } = useQuery({ queryKey: qk.bridge.config, queryFn: BridgeApi.config });
  const { data: catalog = [] } = useQuery({ queryKey: qk.skills.catalog, queryFn: CatalogApi.list });
  const { data: channels = [] } = useQuery({ queryKey: qk.bridge.channels, queryFn: BridgeApi.channels });

  const agentNames = Object.keys(cfg?.agents ?? {});
  const catalogNames = catalog.map((s) => s.name).sort();

  const [name, setName] = useState("");
  const [agent, setAgent] = useState("");
  const [mentions, setMentions] = useState("");
  const [skills, setSkills] = useState<Set<string>>(new Set());
  const [description, setDescription] = useState("");
  const [withRoute, setWithRoute] = useState(false);
  const [channelId, setChannelId] = useState<number | "">("");
  const [chatJid, setChatJid] = useState("");
  const [chatKind, setChatKind] = useState("dm");
  const [mentionRequired, setMentionRequired] = useState(false);
  const [overwrite, setOverwrite] = useState(false);
  const [prefilled, setPrefilled] = useState(false);

  // Prefill once the manifest + cfg arrive.
  useEffect(() => {
    if (prefilled || !manifestFile) return;
    setName((manifest.name ?? "").toLowerCase());
    setAgent(manifest.agent && (cfg?.agents ?? {})[manifest.agent] ? manifest.agent : (agentNames.includes("claude-sub") ? "claude-sub" : agentNames[0] ?? ""));
    setMentions((manifest.mention_names ?? []).join(", "));
    setDescription(manifest.description ?? "");
    setSkills(new Set((manifest.skills ?? []).filter((s) => catalogNames.includes(s))));
    setPrefilled(true);
  }, [manifestFile, manifest, cfg, agentNames, catalogNames, prefilled]);

  const createMut = useMutation({
    mutationFn: () => {
      const body: PersonaCreateInput = {
        name: name.trim(),
        agent,
        mention_names: mentions.split(",").map((s) => s.trim()).filter(Boolean),
        skills: [...skills],
        description: description.trim() || undefined,
        overwrite,
      };
      if (withRoute && channelId !== "" && chatJid.trim()) {
        body.route = { channel_id: Number(channelId), match_chat_jid: chatJid.trim(), match_chat_kind: chatKind, match_mention_required: mentionRequired };
      }
      return PersonaBuilderApi.create(id, body);
    },
    onSuccess: (r) => {
      toast.success(`Created "${r.name}"`, `${r.skills} skill(s)${r.route ? ` · route ${r.route}` : ""}. The bridge will pick it up shortly.`);
      onCreated(r.name);
    },
    onError: (e) => {
      const msg = String(e);
      if (msg.includes("already exists")) { toast.error("Name already exists", "Tick “overwrite” to update the existing persona, or rename."); return; }
      toast.error("Create failed", msg);
    },
  });

  const toggleSkill = (s: string) => setSkills((prev) => { const n = new Set(prev); n.has(s) ? n.delete(s) : n.add(s); return n; });
  const nameValid = /^[a-z0-9][a-z0-9-]{0,31}$/.test(name.trim());
  const routeValid = !withRoute || (channelId !== "" && chatJid.trim().length > 0);
  const canCreate = nameValid && !!agent && routeValid && !createMut.isPending;

  return (
    <div className="absolute inset-0 z-10 bg-bg/80 backdrop-blur-sm flex items-center justify-center p-6" onClick={onClose}>
      <div className="w-full max-w-lg max-h-full overflow-y-auto overscroll-contain rounded-lg border border-border bg-surface shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="px-4 py-3 border-b border-border flex items-center gap-2">
          <UserCheck className="size-4 text-accent" />
          <span className="text-sm font-medium text-fg">Create persona</span>
          <Button size="icon" variant="ghost" className="ml-auto" onClick={onClose} aria-label="Close"><X className="size-4" /></Button>
        </div>
        <div className="p-4 space-y-4">
          <Field label="Name" hint={nameValid ? undefined : "kebab-case: lowercase letters, digits, hyphens"}>
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="bali-concierge"
              className={cn("w-full text-sm rounded-md bg-surface-elevated border p-2 font-mono focus:border-border-strong", nameValid ? "border-border" : "border-danger")} />
          </Field>

          <Field label="Agent backend" hint="The spawn command this persona runs under.">
            <select value={agent} onChange={(e) => setAgent(e.target.value)} className="w-full text-sm rounded-md bg-surface-elevated border border-border p-2 focus:border-border-strong">
              {agentNames.length === 0 && <option value="">(no agents)</option>}
              {agentNames.map((a) => <option key={a} value={a}>{a}</option>)}
            </select>
          </Field>

          <Field label="Mention names" hint="Comma-separated alt names (optional).">
            <input value={mentions} onChange={(e) => setMentions(e.target.value)} placeholder="concierge, front-desk"
              className="w-full text-sm rounded-md bg-surface-elevated border border-border p-2 focus:border-border-strong" />
          </Field>

          <Field label="Description" hint="One-line summary (optional).">
            <input value={description} onChange={(e) => setDescription(e.target.value)}
              className="w-full text-sm rounded-md bg-surface-elevated border border-border p-2 focus:border-border-strong" />
          </Field>

          <Field label={`Skills (${skills.size})`} hint="Symlinked into the persona on create.">
            <div className="max-h-40 overflow-y-auto rounded-md border border-border bg-surface-elevated p-1">
              {catalogNames.length === 0 ? <div className="p-2 text-xs text-fg-muted">No skills in the catalog</div> : catalogNames.map((s) => (
                <label key={s} className="flex items-center gap-2 px-2 py-1 text-xs rounded hover:bg-surface-2 cursor-pointer">
                  <input type="checkbox" checked={skills.has(s)} onChange={() => toggleSkill(s)} />
                  <span className="font-mono text-fg">{s}</span>
                </label>
              ))}
            </div>
          </Field>

          <div className="rounded-md border border-border p-3">
            <label className="flex items-center gap-2 text-sm text-fg cursor-pointer">
              <input type="checkbox" checked={withRoute} onChange={(e) => setWithRoute(e.target.checked)} />
              Bind a route now
            </label>
            {withRoute && (
              <div className="mt-3 space-y-3">
                <Field label="Channel">
                  <select value={channelId} onChange={(e) => setChannelId(e.target.value ? Number(e.target.value) : "")} className="w-full text-sm rounded-md bg-surface-elevated border border-border p-2 focus:border-border-strong">
                    <option value="">Select a channel…</option>
                    {channels.map((ch) => <option key={ch.id} value={ch.id}>{ch.name} ({ch.platform})</option>)}
                  </select>
                </Field>
                <Field label="Chat JID" hint="The chat id this persona answers (e.g. a WhatsApp @lid or GChat space).">
                  <input value={chatJid} onChange={(e) => setChatJid(e.target.value)} placeholder="89601658081466@lid"
                    className="w-full text-sm rounded-md bg-surface-elevated border border-border p-2 font-mono focus:border-border-strong" />
                </Field>
                <div className="flex items-center gap-3">
                  <select value={chatKind} onChange={(e) => setChatKind(e.target.value)} className="text-sm rounded-md bg-surface-elevated border border-border p-2 focus:border-border-strong">
                    <option value="dm">dm</option>
                    <option value="group">group</option>
                  </select>
                  <label className="flex items-center gap-2 text-xs text-fg-muted cursor-pointer">
                    <input type="checkbox" checked={mentionRequired} onChange={(e) => setMentionRequired(e.target.checked)} /> mention required
                  </label>
                </div>
              </div>
            )}
          </div>

          <label className="flex items-center gap-2 text-xs text-fg-muted cursor-pointer">
            <input type="checkbox" checked={overwrite} onChange={(e) => setOverwrite(e.target.checked)} /> overwrite if a persona with this name already exists
          </label>
        </div>
        <div className="px-4 py-3 border-t border-border flex items-center justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={() => createMut.mutate()} disabled={!canCreate} loading={createMut.isPending}>
            <UserCheck className="size-3.5" /> Create persona
          </Button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-xs font-medium text-fg mb-1">{label}</div>
      {children}
      {hint && <div className="text-[11px] text-fg-subtle mt-1">{hint}</div>}
    </div>
  );
}

function Bubble({ role, text }: { role: "assistant" | "tool" | "user"; text: string }) {
  if (role === "tool") return <div className="flex items-center gap-1.5 text-[11px] text-accent font-mono"><FileText className="size-3" /> {text}</div>;
  return (
    <div className={cn("flex", role === "user" ? "justify-end" : "justify-start")}>
      <div className={cn("max-w-[85%] rounded-lg px-3 py-2 text-sm whitespace-pre-wrap [overflow-wrap:anywhere]", role === "user" ? "bg-accent text-fg-inverse" : "bg-surface-elevated text-fg border border-border")}>
        {text}
      </div>
    </div>
  );
}
